import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ThemeConfig } from '#/service/config';
import { ButtonVariant, MenuFocusStyle } from '#/service/constants';
import { remify } from '#/service/utils';

import { getBorderRadius, getVariantStyle } from '../Button/Button';
import { focusShadow } from '../common';

export const transProps = '300ms ease-in-out';

export interface LinkStyleProps {
  theme: ThemeConfig;
  $focusStyle?: MenuFocusStyle;
  $buttonVariant?: ButtonVariant;
  $inHamburger?: boolean;
}

export const linkStyle = ({
  theme,
  $focusStyle = MenuFocusStyle.Box,
  $buttonVariant,
  $inHamburger,
}: LinkStyleProps) => css`
  display: flex;
  position: relative;
  align-items: center;
  text-decoration: none;
  white-space: nowrap;
  height: ${remify($inHamburger ? 40 : 80)};
  box-sizing: border-box;
  cursor: pointer;
  font: var(--fontStyleSubtitle);

  ${!$inHamburger &&
  `
    border-bottom: ${remify(4)} solid transparent;
    border-top: ${remify(4)} solid transparent;
  `}

  & span.title {
    text-overflow: ellipsis;
    max-width: 100%;
    overflow: hidden;
  }

  &:hover span.title,
  &:hover svg {
    opacity: 0.7;
  }

  ${!$buttonVariant &&
  // no button variant -> link style (but override global style), menu colors, active bg, etc
  css`
    &&& {
      color: var(--menuForeground);
      text-decoration: none;
      opacity: 1;
    }

    padding-left: ${remify($inHamburger ? 0 : 24)};

    ${$inHamburger &&
    `
      padding-top: ${remify(8)};
      padding-bottom: ${remify(8)};
      margin: ${remify(16)} 0;
    `}

    &.active {
      ${$focusStyle === MenuFocusStyle.Line &&
      $inHamburger &&
      ` span.box:after {
          content: '';
          position: absolute;
          width: ${remify(4)};
          right: ${remify(-31)};
          height: 100%;
          background: var(--menuHighlightBackground);
        }
      `};

      ${$focusStyle === MenuFocusStyle.Line &&
      !$inHamburger &&
      `border-bottom: ${remify(4)} solid var(--menuHighlightBackground);`};

      ${$focusStyle === MenuFocusStyle.Box &&
      ` > span.box {
          color: var(--menuHighlightForeground);
          background: var(--menuHighlightBackground);
        }
      `};
    }
  `}

  ${$buttonVariant &&
  // button stlye, variant colors, no active bg, etc
  css`
    border: none;
    outline: none;
    cursor: pointer;
    margin: ${remify(20)} 0;
    padding: ${remify(10)};
    background: transparent;
    font: inherit;
    line-height: normal;

    &:hover {
      opacity: 1;
    }

    ${() =>
      $inHamburger
        ? `
            width: 100%;
            & span.box {
              justify-content: center;
            }
          `
        : `
            height: auto;
          `};

    && {
      ${getVariantStyle({ theme, $variant: $buttonVariant })}
      ${getBorderRadius({ theme, $variant: $buttonVariant })}
      ${$buttonVariant === ButtonVariant.Ghost &&
      ` color: var(--menuForeground);`}
    }
  `}
`;

export const MenuGradient = styled.div`
  position: absolute;
  inset: 0;
  padding: 0;
  background: linear-gradient(0deg, transparent, var(--menuGradientBackground));
  z-index: -1;
  pointer-events: none;
  opacity: 1;
  transition: opacity ${transProps};
`;

export const MenuNavLink = styled(NavLink).attrs({
  'data-testid': 'menu-nav-link',
})`
  ${linkStyle};

  &:focus-visible {
    ${focusShadow}
  }
`;

MenuNavLink.displayName = 'MenuNavLink';

export const MenuLink = styled.a`
  ${linkStyle};
`;

MenuLink.displayName = 'MenuLink';

export const MenuButton = styled.button`
  ${linkStyle};

  border-radius: ${remify(5)};

  &:focus-visible {
    ${focusShadow}
  }
`;

MenuButton.displayName = 'MenuButton';

interface MenuItemBoxBgProps {
  $iconOnly?: boolean;
}

export const MenuItemBoxBg = styled.span<MenuItemBoxBgProps>`
  display: flex;
  align-items: center;
  line-height: normal;
  padding: ${remify(9)} ${remify(16)};
  margin: 0 ${remify(-16)};
  border-radius: ${remify(5)};
  min-width: 100%;

  ${({ $iconOnly }) =>
    $iconOnly &&
    `
    padding: ${remify(10)};
    border-radius: 100rem;
  `}
`;

MenuItemBoxBg.displayName = 'MenuItemBoxBg';

interface MenuNavProps {
  $isCollapsed?: boolean;
  $isHidden?: boolean;
  children?: ReactNode;
  theme: ThemeConfig;
}

export const MenuNav = styled.nav<MenuNavProps>`
  font: var(--fontStyleSubtitle);
  display: flex;
  z-index: 1;
  align-items: center;
  color: var(--pageParagraphForeground);
  line-height: ${remify(80)};
  user-select: none;
  background: transparent;
  transition:
    background ${transProps},
    transform ${transProps};

  & > * {
    padding: 0 ${remify(20)};
  }

  & .icon {
    height: 1.3rem;
    width: 1.3rem;
  }

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    `
      padding-bottom: 0;
      background: var(--menuOpaqueBackground);
      pointer-events: auto;
      & > ${MenuGradient} {
        opacity: 0;
      }
    `};

  /* 105% to make sure that even the focus outline is off screen */
  ${({ $isHidden }) => $isHidden && 'transform: translateY(-105%);'}
`;
